import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Link as LinkIcon, FileUp, Trash2, RefreshCw, Loader2, Plus, AlertCircle,
  BookOpen, Sparkles, Database,
} from 'lucide-react';
import { kbApi } from './services/aiChatbotApi';
import { useToast } from '../email/shared/ToastProvider';
import './AIChatbot.css';

const STATUS_LABEL = {
  pending: 'Pending',
  processing: 'Processing',
  indexed: 'Indexed',
  failed: 'Failed',
};

const KnowledgeBase = () => {
  const toast = useToast();
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Add-document form state
  const [showAdd, setShowAdd]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addType, setAddType]   = useState('text'); // text | url | pdf
  const [title, setTitle]       = useState('');
  const [text, setText]         = useState('');
  const [url, setUrl]           = useState('');
  const [pdfFile, setPdfFile]   = useState(null);

  // Skill docs state
  const [skillDocs, setSkillDocs]       = useState([]);
  const [skillLoading, setSkillLoading] = useState(true);
  const [showSkillAdd, setShowSkillAdd] = useState(false);
  const [skillSubmitting, setSkillSubmitting] = useState(false);
  const [skillTitle, setSkillTitle]     = useState('');
  const [skillFile, setSkillFile]       = useState(null);

  /* ── Load list ───────────────────────────────────── */
  const fetchDocs = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await kbApi.list();
      if (res.data?.entity) setDocs(res.data.entity);
    } catch (err) {
      console.error('KB list error:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  const fetchSkillDocs = useCallback(async (silent = false) => {
    try {
      if (!silent) setSkillLoading(true);
      const res = await kbApi.listSkillDocs();
      if (res.data?.entity) setSkillDocs(res.data.entity);
    } catch (err) {
      console.error('Skill docs list error:', err);
    } finally {
      setSkillLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); fetchSkillDocs(); }, [fetchDocs, fetchSkillDocs]);

  // Auto-refresh while any doc is pending/processing
  useEffect(() => {
    const hasPending = docs.some((d) => d.status === 'pending' || d.status === 'processing');
    if (!hasPending) return;
    const t = setInterval(() => fetchDocs(true), 4000);
    return () => clearInterval(t);
  }, [docs, fetchDocs]);

  useEffect(() => {
    const hasPending = skillDocs.some((d) => d.status === 'pending' || d.status === 'processing');
    if (!hasPending) return;
    const t = setInterval(() => fetchSkillDocs(true), 4000);
    return () => clearInterval(t);
  }, [skillDocs, fetchSkillDocs]);

  /* ── Skill doc upload ────────────────────────────── */
  const resetSkillForm = () => { setSkillTitle(''); setSkillFile(null); setShowSkillAdd(false); };

  const onAddSkillDoc = async () => {
    if (!skillTitle.trim()) { toast.error('Title is required'); return; }
    if (!skillFile) { toast.error('File is required (.md or .pdf)'); return; }
    try {
      setSkillSubmitting(true);
      const fd = new FormData();
      fd.append('title', skillTitle.trim());
      fd.append('file', skillFile);
      const res = await kbApi.uploadSkillDoc(fd);
      if (res?.data?.entity) {
        toast.success('Skill doc uploaded — indexing started');
        resetSkillForm();
        fetchSkillDocs();
      } else {
        toast.error(res?.data?.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setSkillSubmitting(false);
    }
  };

  const onDeleteSkillDoc = async (id, docTitle) => {
    if (!window.confirm(`Delete "${docTitle}"? This cannot be undone.`)) return;
    try {
      await kbApi.removeSkillDoc(id);
      toast.success('Skill doc deleted');
      setSkillDocs((d) => d.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  /* ── Add document ────────────────────────────────── */
  const resetAddForm = () => {
    setTitle(''); setText(''); setUrl(''); setPdfFile(null);
    setAddType('text');
    setShowAdd(false);
  };

  const onAdd = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }

    try {
      setSubmitting(true);
      let res;

      if (addType === 'text') {
        if (!text.trim()) { toast.error('Text content is required'); return; }
        res = await kbApi.createText(title.trim(), text);
      } else if (addType === 'url') {
        if (!url.trim()) { toast.error('URL is required'); return; }
        res = await kbApi.createUrl(title.trim(), url.trim());
      } else if (addType === 'pdf') {
        if (!pdfFile) { toast.error('PDF file is required'); return; }
        const fd = new FormData();
        fd.append('title', title.trim());
        fd.append('file', pdfFile);
        res = await kbApi.uploadPdf(fd);
      }

      if (res?.data?.entity) {
        toast.success('Document added — indexing started');
        resetAddForm();
        fetchDocs();
      } else {
        toast.error(res?.data?.message || 'Failed to add document');
      }
    } catch (err) {
      console.error('KB add error:', err);
      toast.error(err.response?.data?.message || 'Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Reindex / Delete ────────────────────────────── */
  const onReindex = async (id) => {
    try {
      await kbApi.reindex(id);
      toast.success('Reindex queued');
      fetchDocs(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reindex');
    }
  };

  const onDelete = async (id, docTitle) => {
    if (!window.confirm(`Delete "${docTitle}"? This cannot be undone.`)) return;
    try {
      await kbApi.remove(id);
      toast.success('Document deleted');
      setDocs((d) => d.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  /* ── Render ──────────────────────────────────────── */
  if (loading) {
    return <div className="ai-loader"><Loader2 size={24} className="spin" /> &nbsp; Loading knowledge base…</div>;
  }

  return (
    <>
      {/* Section heading */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Database size={20} color="#4f46e5" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>Knowledge Base</h2>
          <span style={{ background: '#ede9fe', color: '#6d28d9', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>PRIMARY</span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b', maxWidth: 640 }}>
          The AI searches here <b>first</b> to answer customer questions. Add your FAQs, product docs, pricing, policies, and website content.
        </p>
      </div>
      {/* Add document card */}
      <div className="ai-card">
        <div className="ai-card-header">
          <div>
            <h3 className="ai-card-title">Add Knowledge</h3>
            <p className="ai-card-subtitle">
              Upload FAQs, product docs, or website content. Each document is chunked and embedded for retrieval.
            </p>
          </div>
          {!showAdd && (
            <button className="ai-btn ai-btn-primary" onClick={() => setShowAdd(true)}>
              <Plus size={14} /> Add Document
            </button>
          )}
        </div>

        {showAdd && (
          <>
            <div className="ai-form-row">
              <div className="ai-form-group">
                <label className="ai-label">Source Type</label>
                <select className="ai-select" value={addType} onChange={(e) => setAddType(e.target.value)}>
                  <option value="text">Paste Text</option>
                  <option value="url">Web URL</option>
                  <option value="pdf">PDF Upload</option>
                </select>
              </div>
              <div className="ai-form-group">
                <label className="ai-label">Title</label>
                <input
                  className="ai-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Returns & Refund Policy"
                />
              </div>
            </div>

            {addType === 'text' && (
              <div className="ai-form-group">
                <label className="ai-label">Content</label>
                <textarea
                  className="ai-textarea"
                  rows={8}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste your FAQ content, product details, or any text the bot should learn from…"
                />
                <div className="ai-help">{text.length} characters</div>
              </div>
            )}

            {addType === 'url' && (
              <div className="ai-form-group">
                <label className="ai-label">Web URL</label>
                <input
                  className="ai-input"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/faq"
                />
                <div className="ai-help">The page will be fetched, cleaned, and indexed automatically.</div>
              </div>
            )}

            {addType === 'pdf' && (
              <div className="ai-form-group">
                <label className="ai-label">PDF File</label>
                <input
                  className="ai-input"
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                />
                {pdfFile && <div className="ai-help">{pdfFile.name} • {(pdfFile.size / 1024).toFixed(1)} KB</div>}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="ai-btn ai-btn-secondary" onClick={resetAddForm} disabled={submitting}>
                Cancel
              </button>
              <button className="ai-btn ai-btn-primary" onClick={onAdd} disabled={submitting}>
                {submitting ? <><Loader2 size={14} className="spin" /> Uploading…</> : <><Plus size={14} /> Add Document</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Documents list */}
      <div className="ai-card">
        <div className="ai-card-header">
          <div>
            <h3 className="ai-card-title">Documents</h3>
            <p className="ai-card-subtitle">{docs.length} document{docs.length !== 1 ? 's' : ''} in your knowledge base</p>
          </div>
          <button
            className="ai-btn ai-btn-secondary ai-btn-sm"
            onClick={() => { setRefreshing(true); fetchDocs(); }}
            disabled={refreshing}
          >
            <RefreshCw size={13} className={refreshing ? 'spin' : ''} /> Refresh
          </button>
        </div>

        {docs.length === 0 ? (
          <div className="ai-empty">
            <div className="ai-empty-icon">📚</div>
            <div>No documents yet. Click <b>Add Document</b> above to get started.</div>
          </div>
        ) : (
          <table className="ai-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Title</th>
                <th>Source</th>
                <th>Status</th>
                <th>Chunks</th>
                <th>Added</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <span className="kb-source-icon">
                      {d.source_type === 'pdf' && <FileUp size={16} />}
                      {d.source_type === 'url' && <LinkIcon size={16} />}
                      {(d.source_type === 'text' || d.source_type === 'md') && <FileText size={16} />}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {d.title}
                    {d.error_message && (
                      <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <AlertCircle size={12} /> {d.error_message}
                      </div>
                    )}
                  </td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>
                    {d.source_url
                      ? <a href={d.source_url} target="_blank" rel="noreferrer" style={{ color: '#4f46e5' }}>{d.source_url.slice(0, 30)}…</a>
                      : d.source_type}
                  </td>
                  <td>
                    <span className={`ai-badge ai-badge-${d.status}`}>
                      {(d.status === 'pending' || d.status === 'processing') && <Loader2 size={10} className="spin" />}
                      {STATUS_LABEL[d.status] || d.status}
                    </span>
                  </td>
                  <td>{d.chunk_count ?? 0}</td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>
                    {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="ai-btn ai-btn-secondary ai-btn-sm"
                        onClick={() => onReindex(d.id)}
                        title="Reindex"
                      >
                        <RefreshCw size={12} />
                      </button>
                      <button
                        className="ai-btn ai-btn-danger ai-btn-sm"
                        onClick={() => onDelete(d.id, d.title)}
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Skill Docs section ──────────────────────────── */}
      {/* Section heading */}
      <div style={{ margin: '32px 0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Sparkles size={20} color="#7c3aed" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>Skills</h2>
          <span style={{ background: '#f3e8ff', color: '#7c3aed', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>FALLBACK</span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b', maxWidth: 640 }}>
          Upload specialist guides (.md or .pdf) as a <b>fallback tier</b>. The AI searches these only when the Knowledge Base above doesn't have a good answer — perfect for domain playbooks, SOPs, or expert reference docs.
        </p>
      </div>

      <div className="ai-card">
        <div className="ai-card-header">
          <div>
            <h3 className="ai-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={16} color="#7c3aed" /> Skill Documents
            </h3>
            <p className="ai-card-subtitle">
              Each uploaded file is chunked and embedded. The AI uses these as a secondary source.
            </p>
          </div>
          {!showSkillAdd && (
            <button className="ai-btn ai-btn-primary" onClick={() => setShowSkillAdd(true)}>
              <Plus size={14} /> Upload Skill Doc
            </button>
          )}
        </div>

        {showSkillAdd && (
          <>
            <div className="ai-form-row">
              <div className="ai-form-group">
                <label className="ai-label">Title</label>
                <input
                  className="ai-input"
                  value={skillTitle}
                  onChange={(e) => setSkillTitle(e.target.value)}
                  placeholder="e.g. Customer Support Playbook"
                />
              </div>
              <div className="ai-form-group">
                <label className="ai-label">File (.md or .pdf, max 25 MB)</label>
                <input
                  className="ai-input"
                  type="file"
                  accept=".md,.pdf,application/pdf,text/markdown"
                  onChange={(e) => setSkillFile(e.target.files?.[0] || null)}
                />
                {skillFile && (
                  <div className="ai-help">{skillFile.name} • {(skillFile.size / 1024).toFixed(1)} KB</div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="ai-btn ai-btn-secondary" onClick={resetSkillForm} disabled={skillSubmitting}>
                Cancel
              </button>
              <button className="ai-btn ai-btn-primary" onClick={onAddSkillDoc} disabled={skillSubmitting}>
                {skillSubmitting ? <><Loader2 size={14} className="spin" /> Uploading…</> : <><Plus size={14} /> Upload</>}
              </button>
            </div>
          </>
        )}

        {skillLoading ? (
          <div className="ai-loader"><Loader2 size={20} className="spin" /> &nbsp; Loading skill docs…</div>
        ) : skillDocs.length === 0 ? (
          <div className="ai-empty">
            <div className="ai-empty-icon"><Sparkles size={28} color="#c4b5fd" /></div>
            <div>No skill docs yet. Upload a .md or .pdf file above.</div>
          </div>
        ) : (
          <table className="ai-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Chunks</th>
                <th>Added</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {skillDocs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <span className="kb-source-icon">
                      {d.source_type === 'skill' && <BookOpen size={16} color="#7c3aed" />}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {d.title}
                    {d.error_message && (
                      <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <AlertCircle size={12} /> {d.error_message}
                      </div>
                    )}
                  </td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>
                    {d.s3_key ? 'PDF' : 'Markdown'}
                  </td>
                  <td>
                    <span className={`ai-badge ai-badge-${d.status}`}>
                      {(d.status === 'pending' || d.status === 'processing') && <Loader2 size={10} className="spin" />}
                      {STATUS_LABEL[d.status] || d.status}
                    </span>
                  </td>
                  <td>{d.chunk_count ?? 0}</td>
                  <td style={{ color: '#64748b', fontSize: 13 }}>
                    {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <button
                      className="ai-btn ai-btn-danger ai-btn-sm"
                      onClick={() => onDeleteSkillDoc(d.id, d.title)}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default KnowledgeBase;
